import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { financialReportsAPI, propertyFinanceAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';
import RecordFormModal from '../Common/admin/RecordFormModal';

const money = (value) =>
  value === null || value === undefined
    ? '-'
    : Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const day = (value) => (value ? String(value).slice(0, 10) : '-');

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Income and expenses over a date range, printable.
 *
 * Three shapes share this dialog:
 *   property — one property's statement (passed a property)
 *   summary  — one row per property the caller may see
 *   office   — what the office earned, staff only
 */
const FinancialReportModal = ({ show, onClose, property = null, scope = 'property' }) => {
  const { t } = useTranslation();
  const { isOwner, can } = useAuth();
  const [from, setFrom] = useState(startOfYear);
  const [to, setTo] = useState(today);
  const [mode, setMode] = useState(scope);
  // Kept as { mode, payload }: switching scope changes the shape of the report,
  // and the old payload must not be rendered against the new mode.
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  // { kind: 'income' | 'expense' } — the manual entry dialog.
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    if (!show) return;
    setMode(scope);
    setFrom(startOfYear());
    setTo(today());
    setResult(null);
    setFailure(null);
  }, [show, scope]);

  const load = useCallback(async () => {
    if (!show) return;
    setLoading(true);
    setFailure(null);
    try {
      let response;
      if (mode === 'property' && property?.id) {
        response = await financialReportsAPI.property(property.id, from, to);
      } else if (mode === 'office') {
        response = await financialReportsAPI.office(from, to);
      } else {
        response = await financialReportsAPI.summary(from, to, property?.id || null);
      }
      setResult({ mode, payload: response.data });
    } catch (err) {
      setFailure(apiErrorMessage(err));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [show, mode, property, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const data = result && result.mode === mode ? result.payload : null;

  // Printing targets the report body through a print-only stylesheet.
  const print = () => window.print();

  const entryFields = [
    {
      name: 'date',
      label: t('reports.columns.date'),
      icon: 'fa-calendar-day',
      type: 'date',
      required: true,
    },
    {
      name: 'amount',
      label: t('reports.columns.amount'),
      placeholder: t('reports.columns.amount'),
      icon: 'fa-sack-dollar',
      type: 'number',
      required: true,
    },
    {
      name: 'description',
      label: t('reports.columns.description'),
      placeholder: t('reports.columns.description'),
      icon: 'fa-align-right',
      type: 'text',
      required: true,
      full: true,
    },
    {
      name: 'reference',
      label: t('reports.reference'),
      placeholder: t('reports.reference'),
      icon: 'fa-hashtag',
      type: 'text',
    },
  ];

  const saveEntry = async (values) => {
    const payload = {
      propertyId: property.id,
      date: values.date,
      description: values.description,
      amount: Number(values.amount),
      reference: values.reference || null,
    };

    if (entry.kind === 'income') await propertyFinanceAPI.createIncome(payload);
    else await propertyFinanceAPI.createExpense(payload);

    setEntry(null);
    load();
  };

  // Manual entries belong to one property, and owners never write.
  const canEdit = mode === 'property' && Boolean(property?.id) && !isOwner && can('properties', 'Edit');

  const title =
    mode === 'office'
      ? t('reports.officeTitle')
      : mode === 'summary'
      ? t('reports.summaryTitle')
      : t('reports.propertyTitle');

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const lineRows = (lines, emptyKey) =>
    lines.length === 0 ? (
      <tr>
        <td colSpan="4" className="ad-ledger-empty">
          {t(emptyKey)}
        </td>
      </tr>
    ) : (
      lines.map((line, index) => (
        <tr key={`${line.date}-${index}`}>
          <td>{day(line.date)}</td>
          <td>{line.description || '-'}</td>
          <td>{t(`reports.categories.${line.category}`, line.category)}</td>
          <td>{money(line.amount)}</td>
        </tr>
      ))
    );

  return (
    <>
    <Modal
      show={show}
      title={title}
      subtitle={mode === 'property' ? property?.label : undefined}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ad-btn ad-btn-ghost" onClick={print} disabled={!data}>
            <i className="fas fa-print"></i>
            {t('common.print')}
          </button>
          <button type="button" className="ad-btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      <div className="ad-report">
        <div className="ad-report-controls">
          <div className="ad-modal-field">
            <label className="ad-modal-label">{t('reports.from')}</label>
            <span className="ad-input-wrap">
              <i className="fas fa-calendar-day ad-input-icon"></i>
              <input
                type="date"
                className="ad-input-sm ad-input-has-icon"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </span>
          </div>

          <div className="ad-modal-field">
            <label className="ad-modal-label">{t('reports.to')}</label>
            <span className="ad-input-wrap">
              <i className="fas fa-calendar-check ad-input-icon"></i>
              <input
                type="date"
                className="ad-input-sm ad-input-has-icon"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </span>
          </div>

          <div className="ad-report-scopes">
            {property && (
              <button
                type="button"
                className={`ad-btn ${mode === 'property' ? 'ad-btn-primary' : ''}`}
                onClick={() => setMode('property')}
              >
                {t('reports.scopeProperty')}
              </button>
            )}
            <button
              type="button"
              className={`ad-btn ${mode === 'summary' ? 'ad-btn-primary' : ''}`}
              onClick={() => setMode('summary')}
            >
              {t('reports.scopeAll')}
            </button>
            {!isOwner && (
              <button
                type="button"
                className={`ad-btn ${mode === 'office' ? 'ad-btn-primary' : ''}`}
                onClick={() => setMode('office')}
              >
                {t('reports.scopeOffice')}
              </button>
            )}
          </div>
        </div>

        {failure && <p className="ad-error-text">{failure}</p>}
        {loading && <p className="ad-column-note">{t('tree.loading')}</p>}

        {!loading && data && (
          <div className="ad-report-body" id="ad-report-print">
            <div className="ad-report-head">
              <h3>{title}</h3>
              <span>
                {day(data.from)} — {day(data.to)}
              </span>
              {mode === 'property' && data.property && (
                <span>
                  {data.property.nameAr || data.property.nameEng} ({data.property.code})
                </span>
              )}
            </div>

            {mode === 'summary' ? (
              <table className="ad-ledger-table">
                <thead>
                  <tr>
                    <th>{t('reports.columns.property')}</th>
                    <th>{t('reports.columns.income')}</th>
                    <th>{t('reports.columns.expenses')}</th>
                    <th>{t('reports.columns.commission')}</th>
                    <th>{t('reports.columns.net')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan="5" className="ad-ledger-empty">
                        {t('reports.noData')}
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.nameAr || row.nameEng || row.code}</td>
                      <td>{money(row.income)}</td>
                      <td>{money(row.expenses)}</td>
                      <td>{money(row.commission)}</td>
                      <td>{money(row.netToOwner)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t('reports.total')}</td>
                    <td>{money(totals.income)}</td>
                    <td>{money(totals.expenses)}</td>
                    <td>{money(totals.commission)}</td>
                    <td>{money(totals.netToOwner)}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className="ad-report-columns">
                <section>
                  <h4 className="ad-report-section">
                    {t('reports.income')}
                    {canEdit && (
                      <button
                        type="button"
                        className="ad-column-add"
                        onClick={() => setEntry({ kind: 'income' })}
                        title={t('reports.addIncome')}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    )}
                  </h4>
                  <table className="ad-ledger-table">
                    <thead>
                      <tr>
                        <th>{t('reports.columns.date')}</th>
                        <th>{t('reports.columns.description')}</th>
                        <th>{t('reports.columns.category')}</th>
                        <th>{t('reports.columns.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>{lineRows(data.income || [], 'reports.noIncome')}</tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3">{t('reports.total')}</td>
                        <td>{money(totals.income)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </section>

                <section>
                  <h4 className="ad-report-section">
                    {t('reports.expenses')}
                    {canEdit && (
                      <button
                        type="button"
                        className="ad-column-add"
                        onClick={() => setEntry({ kind: 'expense' })}
                        title={t('reports.addExpense')}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    )}
                  </h4>
                  <table className="ad-ledger-table">
                    <thead>
                      <tr>
                        <th>{t('reports.columns.date')}</th>
                        <th>{t('reports.columns.description')}</th>
                        <th>{t('reports.columns.category')}</th>
                        <th>{t('reports.columns.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>{lineRows(data.expenses || [], 'reports.noExpenses')}</tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3">{t('reports.total')}</td>
                        <td>{money(totals.expenses)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </section>
              </div>
            )}

            <div className="ad-report-totals">
              {mode === 'property' && data.commission && (
                <span>
                  {t('reports.commission')}
                  {data.commission.isPercentage
                    ? ` (${data.commission.commissionPercentage ?? 0}%)`
                    : ` (${t('settings.fixed')})`}
                  : <strong>{money(data.commission.total)}</strong>
                </span>
              )}
              <span>
                {t('reports.income')}: <strong>{money(totals.income)}</strong>
              </span>
              <span>
                {t('reports.expenses')}: <strong>{money(totals.expenses)}</strong>
              </span>
              <span className="ad-report-net">
                {mode === 'office' ? t('reports.profit') : t('reports.netToOwner')}:{' '}
                <strong>
                  {money(mode === 'office' ? totals.profit : totals.netToOwner)}
                </strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>

      <RecordFormModal
        show={entry !== null}
        title={entry?.kind === 'income' ? t('reports.addIncome') : t('reports.addExpense')}
        fields={entryFields}
        initial={null}
        onSubmit={saveEntry}
        onClose={() => setEntry(null)}
      />
    </>
  );
};

export default FinancialReportModal;
